/**
 * Centralized date/time formatting used across the operator console.
 *
 * Goal: operators should always be able to tell WHEN something happened
 * by looking at the timestamp — never "12d ago" with no anchor.
 *
 * The standard treatment for any timestamp column is:
 *   primary line   →  formatAbsolute(iso)   "May 21, 14:32"
 *   secondary line →  formatRelative(iso)   "3 hours ago"
 *
 * Both come from these helpers.
 */

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Absolute timestamp. Drops the year when the timestamp is in the current
 * calendar year (matches Mail / Linear conventions).
 *
 *   today 14:32          → "Today 14:32"
 *   yesterday 09:17      → "Yesterday 09:17"
 *   this year (May 19)   → "May 19, 14:32"
 *   prior year (2025)    → "Dec 4, 2025, 14:32"
 */
export function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const daysAgo = Math.round((today.getTime() - dDay.getTime()) / 86_400_000);
  const hm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

  if (daysAgo === 0) return `Today ${hm}`;
  if (daysAgo === 1) return `Yesterday ${hm}`;

  const sameYear = d.getFullYear() === now.getFullYear();
  const monthDay = `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
  if (sameYear) return `${monthDay}, ${hm}`;
  return `${monthDay}, ${d.getFullYear()}, ${hm}`;
}

/**
 * Relative — short, scannable, no "ago" suffix.
 *   < 60s   → "just now"
 *   < 60m   → "12m"
 *   < 24h   → "3h"
 *   < 7d    → "2d"
 *   else    → "" (caller should fall back to absolute only)
 */
export function formatRelativeShort(iso: string): string {
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "";
  const secs = Math.floor((Date.now() - d) / 1000);
  if (secs < 60) return "just now";
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return "";
}

/**
 * Longer relative form for tooltip / secondary line.
 *   < 60s   → "just now"
 *   < 60m   → "12 minutes ago"
 *   < 24h   → "3 hours ago"
 *   < 7d    → "2 days ago"
 *   else    → "Mar 14"  (fall back to absolute month/day)
 */
export function formatRelative(iso: string): string {
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "";
  const secs = Math.floor((Date.now() - d) / 1000);
  if (secs < 60) return "just now";
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatAbsolute(iso);
}

/**
 * Full tooltip form — "Wednesday, May 21, 2026, 14:32:18 UTC -0700".
 * Used for the title= attribute when operators need exact timestamps
 * for audit reasons.
 */
export function formatTooltip(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
}
